import { MutationCache, QueryCache } from "@tanstack/react-query";
import { UnauthenticatedError, redirectToLogin } from "./api.js";

/**
 * GT-FI-MMP-COMPLETION-01 Wave 1 — admin-app mirror of
 * `apps/web/src/lib/unauthenticatedHandler.ts`.
 *
 * Same architectural gap: `SessionProvider.tsx` redirects on its
 * initial `/v1/auth/session` fetch, but every later query hook that
 * receives a mid-session 401 (e.g. after the access token expires
 * and the inline refresh grant fails) currently surfaces the raw
 * error to the operator instead of re-authenticating.
 *
 * This module wires `UnauthenticatedError` → `redirectToLogin()`
 * directly into the QueryClient's caches so the admin console
 * recovers transparently, matching the documented contract in
 * `apps/admin/src/lib/api.ts`.
 *
 * Tests: `apps/admin/src/lib/unauthenticatedHandler.test.ts`.
 */

let redirectInFlight = false;

/** Test-only reset. Production code must not call this. */
export function __resetUnauthenticatedRedirectForTests(): void {
  redirectInFlight = false;
}

function onCacheError(err: unknown): void {
  if (!(err instanceof UnauthenticatedError)) return;
  if (redirectInFlight) return;
  redirectInFlight = true;
  redirectToLogin();
}

export function buildUnauthenticatedAwareQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error) => onCacheError(error),
  });
}

export function buildUnauthenticatedAwareMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error) => onCacheError(error),
  });
}
